/**
 * Sentry SDK initialization for the frontend (React SPA on Cloudflare Pages).
 *
 * Features:
 * - Automatic error capture (unhandled exceptions + promise rejections)
 * - Browser performance tracing (page loads, navigation)
 * - Session replay on errors (100% of error sessions, 0% of normal sessions)
 * - Source map integration via @sentry/vite-plugin at build time
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';

export function initSentry(): void {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance: trace 20% of page loads
    tracesSampleRate: 0.2,

    // Session Replay: only record sessions that have errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Don't report errors from browser extensions or third-party scripts
    beforeSend(event) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;
      if (frames?.some((f) => f.filename?.includes('extensions://'))) {
        return null;
      }
      return event;
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Load failed',
      'Failed to fetch',
      'NetworkError',
      'AbortError',
    ],
  });
}

export { Sentry };
